import { createContext, useContext, createEffect, onMount, onCleanup, type ParentProps } from "solid-js";
import { createStore } from "solid-js/store";
import type { Session, Subscription } from "@supabase/supabase-js";
import { supabase, signInWithDiscord, signOut as supabaseSignOut, setDevToken, getAccessToken } from "./supabase";
import { authApi, ApiResponseError } from "./api";
import { subscribeToUserRow } from "./realtime";
import type { User } from "system";

export interface AuthState {
  session: Session | null;
  user: User | null;
  loading: boolean;
  requiresJoin: boolean;
  inviteUrl: string | null;
}

interface AuthContext {
  state: AuthState;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  devLogin: (userId: string) => Promise<void>;
}

const AuthCtx = createContext<AuthContext>();

export function AuthProvider(props: ParentProps) {
  const [state, setState] = createStore<AuthState>({
    session: null,
    user: null,
    loading: true,
    requiresJoin: false,
    inviteUrl: null,
  });

  async function syncUser(session: Session | null) {
    if (!session) {
      if (import.meta.env.DEV) {
        const token = await getAccessToken();
        if (token) {
          try {
            const user = await authApi.me();
            setState({ user, loading: false, requiresJoin: false, inviteUrl: null });
            return;
          } catch {
            setDevToken(null);
          }
        }
      }
      setState({ user: null, loading: false, requiresJoin: false, inviteUrl: null });
      return;
    }
    try {
      const user = await authApi.sync(session.access_token, session.provider_token);
      setState({ user, loading: false, requiresJoin: false, inviteUrl: null });
    } catch (err) {
      if (err instanceof ApiResponseError && err.status === 403 && err.data.requiresJoin) {
        setState({
          user: null,
          loading: false,
          requiresJoin: true,
          inviteUrl: (err.data.inviteUrl as string) ?? null,
        });
        return;
      }
      setState({ user: null, loading: false, requiresJoin: false, inviteUrl: null });
    }
  }

  async function refreshUser() {
    const { data } = await supabase.auth.getSession();
    await syncUser(data.session);
  }

  let subscription: Subscription;

  onMount(() => {
    const { data: { subscription: s } } = supabase.auth.onAuthStateChange(async (_, session) => {
      setState("session", session);
      await syncUser(session);
    });

    subscription = s;
  });

  onCleanup(() => subscription.unsubscribe());

  // Keep the logged-in user's own row live: a Discord role change (or any other
  // update to their row) writes the users table; we refetch and update the store
  // so the UI reflects it without a reload. Server already enforces role per
  // request, so this only refreshes the client's own view. Re-subscribes when the
  // user id changes; tears down on logout.
  createEffect(() => {
    const id = state.user?.id;
    if (!id) return;
    const unsub = subscribeToUserRow(id, async () => {
      try {
        const user = await authApi.me();
        setState("user", user);
      } catch {
        // Transient fetch error - the next change or a reload reconciles it.
      }
    });
    onCleanup(unsub);
  });

  async function signIn() {
    await signInWithDiscord();
  }

  async function signOut() {
    if (import.meta.env.DEV) setDevToken(null);
    await supabaseSignOut();
    setState({ session: null, user: null });
  }

  async function devLogin(userId: string) {
    const res = await fetch("/api/auth/dev/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId }),
    });
    if (!res.ok) throw new Error("Dev login failed");
    const { token, user } = await res.json() as { token: string; user: User };
    setDevToken(token);
    setState({ user, loading: false, requiresJoin: false, inviteUrl: null });
  }

  return (
    <AuthCtx.Provider value={{ state, signIn, signOut, refreshUser, devLogin }}>
      {props.children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
